import { useEffect, useState, type ReactElement, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AppWindow } from "lucide-react";
import { Button, EmptyState, Wordmark } from "@playbookai/ui";
import { PRODUCT_NAME } from "@playbookai/types";
import { ME_QUERY_KEY } from "./auth-store.js";
import {
  useSignInElsewhere,
  type SignInStartFailure,
} from "./sign-in-start.js";

/** The product mark at the top of the column on the standalone auth pages
 *  (`/auth/callback`, `/auth/restart`, `/auth/sign-out`). */
export const authProductMark = (
  <div className="mb-3 text-pb-text">
    <Wordmark label={PRODUCT_NAME} />
  </div>
);

/**
 * The screen for a sign-in that could not be started (PB-5461). Plain words
 * only: the failure arrives as one of two kinds, never as a status or an error
 * string. While the one automatic retry is pending the wait counts down, and
 * Try again does not have to wait for it.
 */
export function SignInStartFailureScreen({
  failure,
  onTryAgain,
}: {
  readonly failure: SignInStartFailure;
  readonly onTryAgain: () => void;
}): ReactElement {
  const retryAt = failure.kind === "busy" ? failure.retryAt : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (retryAt === null) return;
    setNow(Date.now());
    const tick = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(tick);
  }, [retryAt]);

  let description = "The sign-in service is not responding.";
  if (failure.kind === "busy") {
    if (retryAt === null) {
      description =
        "Many people are signing in right now. Try again in a moment.";
    } else {
      const seconds = Math.max(1, Math.ceil((retryAt - now) / 1_000));
      description = `Many people are signing in right now. Trying again in ${seconds} ${
        seconds === 1 ? "second" : "seconds"
      }.`;
    }
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-2 text-pb-text-secondary">
      {/* The page-level failure screen: EmptyState lg (OK-33). The alert is
          the title alone, so the countdown below it is not re-announced every
          second. */}
      <EmptyState
        variant="error"
        size="lg"
        title={<span role="alert">Couldn&rsquo;t start sign-in.</span>}
        description={description}
        action={
          <Button variant="secondary" onClick={onTryAgain}>
            Try again
          </Button>
        }
      />
    </div>
  );
}

/**
 * The screen for a tab that waits while another tab signs in (PB-5461, d21).
 * Only one tab at a time goes to Keycloak; this one never went, so its address
 * is still its own page and it continues there as soon as /me finds the other
 * tab's session. While mounted it keeps asking (see `useSignInElsewhere`).
 *
 * "Sign in here instead" is the way out when the other tab was closed or
 * forgotten: it takes the lease and signs in from this tab.
 */
export function SignInElsewhereScreen({
  onSignedIn,
  onLeaseFree,
  onSignInHere,
  busy = false,
  mark = null,
  children = null,
}: {
  readonly onSignedIn: () => void;
  readonly onLeaseFree: () => void;
  readonly onSignInHere: () => void;
  /** A sign-in from this tab is on its way. */
  readonly busy?: boolean;
  /** The product mark, on the standalone auth pages. */
  readonly mark?: ReactNode;
  /** Shown under the panel, e.g. a failed "Sign in here instead". */
  readonly children?: ReactNode;
}): ReactElement {
  useSignInElsewhere({ onSignedIn, onLeaseFree });
  return (
    <div className="pb-motion-content-enter flex h-screen flex-col items-center justify-center gap-3 px-6 text-center text-pb-text-secondary">
      {mark}
      {/* A wait, not a failure: a polite status rather than an alert. */}
      <EmptyState
        size="lg"
        icon={<AppWindow className="h-6 w-6" />}
        title={<span role="status">Signing in in another tab…</span>}
        description="Finish signing in there, and this page will continue."
        action={
          <Button variant="secondary" disabled={busy} onClick={onSignInHere}>
            {busy ? "Opening sign-in…" : "Sign in here instead"}
          </Button>
        }
      />
      {children}
    </div>
  );
}

/**
 * The screen for a session check that never got an answer (PB-5206): `/me`
 * failed every attempt, so whether this browser is signed in is still unknown.
 * It is not a sign-out and must never start a sign-in (PB-4900), so the one
 * way forward it offers is asking again.
 *
 * Shared by the page guard and `/auth/sign-out`, the two places that cannot go
 * on without knowing.
 */
export function SessionUnavailableScreen(): ReactElement {
  const queryClient = useQueryClient();
  const [rechecking, setRechecking] = useState(false);

  const recheck = (): void => {
    if (rechecking) return;
    setRechecking(true);
    // `refetchQueries`, not `invalidateQueries`. Both would fire here —
    // invalidation marks a query invalidated whether or not it holds data,
    // and its default `refetchType: 'active'` covers this one because the
    // caller is observing it. The preference is that this button means "ask
    // again", so it says so directly rather than routing the request through a
    // staleness flag. The query's own retry policy applies again underneath.
    void queryClient.refetchQueries({ queryKey: ME_QUERY_KEY }).finally(() => {
      setRechecking(false);
    });
  };

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3 px-6 text-center text-pb-text-secondary">
      {/* The page-level failure screen: EmptyState lg (OK-33). The live region
          is the MESSAGE, not the panel, so role="alert" sits on the title text
          alone. A role="alert" wrapping the button re-announces the whole
          region on every Try again/Checking… label swap, and assistive tech
          does not reliably expose a focusable control from inside a live
          region's virtual buffer. */}
      <EmptyState
        variant="error"
        size="lg"
        title={<span role="alert">Can&rsquo;t reach the sign-in service.</span>}
        description="This is usually short. Your work is not lost."
        action={
          <Button variant="secondary" disabled={rechecking} onClick={recheck}>
            {rechecking ? "Checking…" : "Try again"}
          </Button>
        }
      />
    </div>
  );
}
